<template>
    <DModal
        :show="showModal"
        :is-awaiting-validation="true"
        :title="__('tools.testing.snapshots.compare.modal.title')"
        :message="__('tools.testing.snapshots.compare.modal.message', {'snapshot': displayName ?? item})"
        is-confirm
        is-medium
        @close="close"
        @confirm="confirm"
    >
        <div class="modal-content px-4 mb-4">
            <DAlert
                v-if="errors && errors.general_error"
                color="danger"
                class="mt-2"
            >
                {{ errors.general_error }}
            </DAlert>
            <DFormRow
                v-if="sections.settings === true"
                :label="__('tools.testing.snapshots.compare.modal.form.settings')"
                is-required
                :error="errors?.settings"
                :hint="__('tools.testing.snapshots.compare.modal.form.settings_hint')"
            >
                <DSwitch
                    id="settings"
                    v-model="form.settings"
                    color="success"
                />
            </DFormRow>
            <DFormRow
                v-if="sections.products === true"
                :label="__('tools.testing.snapshots.compare.modal.form.products')"
                is-required
                :error="errors?.products"
                :hint="__('tools.testing.snapshots.compare.modal.form.products_hint')"
            >
                <DSwitch
                    id="products"
                    v-model="form.products"
                    color="success"
                />
            </DFormRow>
            <DFormRow
                v-if="sections.email_templates === true"
                :label="__('tools.testing.snapshots.compare.modal.form.email_templates')"
                is-required
                :error="errors?.email_templates"
                :hint="__('tools.testing.snapshots.compare.modal.form.email_templates_hint')"
            >
                <DSwitch
                    id="email_templates"
                    v-model="form.emailTemplates"
                    color="success"
                />
            </DFormRow>
            <DFormRow
                v-if="sections.sms_templates === true"
                :label="__('tools.testing.snapshots.compare.modal.form.sms_templates')"
                is-required
                :error="errors?.sms_templates"
                :hint="__('tools.testing.snapshots.compare.modal.form.sms_templates_hint')"
            >
                <DSwitch
                    id="sms_templates"
                    v-model="form.smsTemplates"
                    color="success"
                />
            </DFormRow>
            <DFormRow
                v-if="sections.customers === true"
                :label="__('tools.testing.snapshots.compare.modal.form.customers')"
                is-required
                :error="errors?.customers"
                :hint="__('tools.testing.snapshots.compare.modal.form.customers_hint')"
            >
                <DSwitch
                    id="customers"
                    v-model="form.customers"
                    color="success"
                />
            </DFormRow>
            <div class="mb-3 mt-2">
                <label class="form-label fw-semibold">{{ __('tools.testing.snapshots.compare.modal.form.apply_mode') }}</label>
                <div>
                    <DFormCheck
                        id="apply_mode_replace"
                        type="radio"
                        name="applyMode"
                        :label="__('tools.testing.snapshots.compare.modal.form.apply_mode_replace')"
                        :checked="form.applyMode === 'replace'"
                        @change="form.applyMode = 'replace'"
                    />
                    <p class="text-muted small ms-4 mt-0 mb-2">
                        {{ __('tools.testing.snapshots.compare.modal.form.apply_mode_replace_hint') }}
                    </p>
                    <DFormCheck
                        id="apply_mode_insert"
                        type="radio"
                        name="applyMode"
                        :label="__('tools.testing.snapshots.compare.modal.form.apply_mode_insert')"
                        :checked="form.applyMode === 'insert'"
                        @change="form.applyMode = 'insert'"
                    />
                    <p class="text-muted small ms-4 mt-0 mb-0">
                        {{ __('tools.testing.snapshots.compare.modal.form.apply_mode_insert_hint') }}
                    </p>
                </div>
            </div>
        </div>
    </DModal>
</template>

<script setup>

import { __ } from '~@/Composables/translation'
import { useForm } from '@inertiajs/vue3'
import { ref } from 'vue'

const props = defineProps({
    showModal: {
        type: Boolean,
        default: false
    },
    item: {
        type: String,
        default: ''
    },
    sections: {
        type: Object,
        default: () => { return {} }
    },
    displayName: {
        type: String,
        default: ''
    },
    includeSecrets: {
        type: Boolean,
        default: false
    },
    errors: {
        type: Object,
        default: () => { return {} }
    }
})

const itemRef = ref(props.item)
const secretsRef = ref(props.includeSecrets)
const emit = defineEmits(['close', 'confirm'])

const form = useForm({
    settings: props.sections.settings ?? false,
    products: props.sections.products ?? false,
    emailTemplates: props.sections.email_templates ?? false,
    smsTemplates: props.sections.sms_templates ?? false,
    customers: props.sections.customers ?? false,
    applyMode: 'replace',
})

function confirm () {
    const data = {}

    // Only include sections that are available
    if (props.sections.settings === true) {
        data.settings = form.settings ?? false
    }
    if (props.sections.products === true) {
        data.products = form.products ?? false
    }
    if (props.sections.email_templates === true) {
        data.emailTemplates = form.emailTemplates ?? false
    }
    if (props.sections.sms_templates === true) {
        data.smsTemplates = form.smsTemplates ?? false
    }
    if (props.sections.customers === true) {
        data.customers = form.customers ?? false
    }

    emit('confirm', itemRef.value, data, secretsRef.value, form.applyMode)
}

function close () {
    emit('close')
    form.reset()
}
</script>

<style lang="scss" scoped>
.modal-content {
    box-shadow: none;
}

.modal-content h2, .modal-content p {
    color: var(--cui-body-color);
    opacity: 0.6;
}
</style>
